import { datadogRum } from '@datadog/browser-rum';
import { reactPlugin } from '@datadog/browser-rum-react';
import { FullStory, init, isInitialized } from '@fullstory/browser';
import * as Sentry from '@sentry/react';
import { isAxiosError } from 'axios';

import analytics from '../../services/analytics';
import colors from '../../theme/colors';
import { spacings } from '../../theme/variables';

const setupConfig = (config: Record<string, any>) => {
  if (config && Object.keys(window.__amp_config).length === 0) {
    window.__amp_config = config;
  }
};

const setupAnalytics = (config: Record<string, any>) => analytics.load(config);

const setScrollbarStyle = (appEnv: 'staging' | 'production') => {
  if (appEnv === 'production') {
    return;
  }

  const scrollbarStylesheet = document.createElement('style');
  scrollbarStylesheet.textContent = `
  html {
    scrollbar-width: thin;
    scrollbar-color: #B3B3B3 transparent;
  }`;

  document.head.appendChild(scrollbarStylesheet);
};

const setupMaskHighlight = (appEnv: 'staging' | 'production') => {
  if (appEnv === 'production') {
    return;
  }

  const STYLESHEET_ID = 'fs-mask-stylesheet';

  const highlightMaskStylesheet = document.createElement('style');
  highlightMaskStylesheet.id = STYLESHEET_ID;
  highlightMaskStylesheet.textContent = `
      .fs-mask {
        background: repeating-linear-gradient(
          45deg,
          ${colors.white},
          ${colors.white} ${spacings.px6}px,
          ${colors.border} ${spacings.px6}px,
          ${colors.border} ${spacings.px12}px
        );
        outline: 1px solid ${colors.border};
      }
    `;
  document.addEventListener('keydown', event => {
    // Check if the shift + option + Z key is pressed
    if (event.shiftKey && event.altKey && event.code === 'KeyZ') {
      if (document.getElementById(STYLESHEET_ID)) {
        document.getElementById(STYLESHEET_ID)?.remove();
      } else {
        document.head.appendChild(highlightMaskStylesheet);
      }
    }
  });
};

const IGNORED_ERRORS = [
  'ResizeObserver loop completed with undelivered notifications',
  "Failed to execute 'importScripts' on 'WorkerGlobalScope'"
];

const setupSentry = (config: Record<string, any>) => {
  if (import.meta.env.PROD && config.sentry) {
    Sentry.init({
      dsn: config.sentry_dsn,
      environment: config.sentry_environment,
      release: import.meta.env.VITE_APP_SENTRY_RELEASE,
      normalizeDepth: 10,
      denyUrls: [/^chrome:\/\//i, /^chrome-extension:\/\//i],
      beforeSend: (event, hint) => {
        const error = hint.originalException as Error;

        if (error?.message?.includes('Failed to fetch dynamically imported module')) {
          event.fingerprint = ['failed-to-fetch-dynamically-imported-module'];
        }

        if (error?.message?.includes('Unable to preload CSS')) {
          event.fingerprint = ['unable-to-preload-css'];
        }

        if (isAxiosError(error)) {
          return null;
        }

        if (IGNORED_ERRORS.some(substring => error?.message?.includes(substring))) {
          return null;
        }

        if (isInitialized()) {
          const sessionUrl = FullStory('getSession', { format: 'url.now' });
          if (sessionUrl) {
            event.extra = { ...event.extra, fullstoryURL: sessionUrl };
          }
        }

        return event;
      }
    });
  }
};

const setupFullStory = (config: Record<string, any>) => {
  if (import.meta.env.PROD && config.fullstory) {
    init({ orgId: config.fullstory_org_id });
  }
};

const setupDatadogMonitoring = (config: Record<string, any>) => {
  if (import.meta.env.PROD) {
    datadogRum.init({
      applicationId: config.datadog_rum_application_id,
      clientToken: config.datadog_rum_client_token,
      site: 'datadoghq.com',
      service: 'amp',
      env: config.datadog_rum_env,
      version: '0.0.3',
      sessionSampleRate: 50,
      sessionReplaySampleRate: 0,
      defaultPrivacyLevel: 'mask-user-input',
      plugins: [reactPlugin({ router: false })],
      trackResources: true,
      trackLongTasks: true,
      allowedTracingUrls: [url => url.includes('amp-st1.matic') || url.includes('amp.matic')],
      traceSampleRate: 0,
      traceContextInjection: 'sampled'
    });
  }
};

export {
  setScrollbarStyle,
  setupAnalytics,
  setupConfig,
  setupDatadogMonitoring,
  setupFullStory,
  setupMaskHighlight,
  setupSentry
};
